import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../API';
import SetupPageLayout from '../setup/SetupPageLayout';
import WizardFooter from '../wizard/WizardFooter';
import styles from './RegionDefinitionWizard.module.css';

function RegionDefinitionWizard({ initialItem = null, onComplete, onCancel }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isEditMode = !!initialItem;
    
    // Initialize step from URL once, then maintain in state
    const [currentStep, setCurrentStep] = useState(() => {
        const urlStep = searchParams.get('wizardStep');
        const schemaKey = searchParams.get('schemaKey');
        if (urlStep) {
            const step = parseInt(urlStep);
            // Cap at step 2 (we removed step 3)
            return step > 2 ? 2 : step;
        }
        // If schemaKey is provided, start at step 2
        if (schemaKey) {
            return 2;
        }
        return isEditMode ? 2 : 1;
    });
    const schemaKeyFromUrl = searchParams.get('schemaKey');
    const [selectedRegionKey, setSelectedRegionKey] = useState(schemaKeyFromUrl || '');
    
    // Use useMemo for initial form data (create mode only)
    const initialFormData = useMemo(() => {
        // Load saved orientation preferences in create mode
        let savedPreferences = null;
        if (!initialItem) {
            try {
                const saved = localStorage.getItem('bdsa_region_orientation_preferences');
                if (saved) {
                    savedPreferences = JSON.parse(saved);
                }
            } catch (e) {
                console.error('Error loading region orientation preferences:', e);
            }
        }
        
        return {
            displayName: '',
            abbreviation: '',
            description: '',
            selectedLandmarks: [],
            schemaFields: {
                hemisphere: savedPreferences?.hemisphere || '',
                sliceOrientation: savedPreferences?.sliceOrientation || '',
                damage: [],
            },
            otherLesionFields: {
                regionCategory: '',
            },
        };
    }, [initialItem]);
    
    const [formData, setFormData] = useState(initialFormData);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [rememberOrientation, setRememberOrientation] = useState(false);
    const [damageEnabled, setDamageEnabled] = useState(() => {
        // Enable damage by default if editing and damage has values
        if (initialItem?.details?.damage && Array.isArray(initialItem.details.damage) && initialItem.details.damage.length > 0) {
            return true;
        }
        return false;
    });

    // Fetch published schemas
    const { data: schemasResponse, isLoading: schemasLoading } = useQuery({
        queryKey: ['bdsa-schema', 'region'],
        queryFn: () => api.get('/bdsa/schema', { query: { name: 'region', includeDrafts: false } }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch existing region definitions
    const { data: regionsResponse, isLoading: regionsLoading } = useQuery({
        queryKey: ['bdsa-region'],
        queryFn: () => api.get('/bdsa/region/template'),
        staleTime: 5 * 60 * 1000,
    });

    // Automatically select latest schema
    const latestSchema = useMemo(() => {
        if (schemasResponse?.status === 200 && Array.isArray(schemasResponse.data) && schemasResponse.data.length > 0) {
            return schemasResponse.data.sort((a, b) => (b.version || 0) - (a.version || 0))[0];
        }
        return null;
    }, [schemasResponse]);

    const createRegionMutation = useMutation({
        mutationFn: async (regionData) => {
            if (isEditMode) {
                const response = await api.put(`/bdsa/region/template/${initialItem._id}`, {
                    body: {
                        abbreviation: regionData.query.abbreviation,
                        display_name: regionData.body.display_name,
                        schema_region_key: regionData.body.schema_region_key,
                        schema_id: regionData.body.schema_id,
                        description: regionData.body.description,
                        details: regionData.body.details,
                        used_from: regionData.body.used_from,
                        used_to: regionData.body.used_to,
                    },
                });
                if (response.status !== 200) {
                    const errorMessage = response.data?.message || 
                                       response.data?.error || 
                                       (typeof response.data === 'string' ? response.data : null) ||
                                       response.data?.statusText ||
                                       `Failed to update region definition (${response.status})`;
                    const error = new Error(errorMessage);
                    error.response = response;
                    throw error;
                }
                return response;
            } else {
                const response = await api.post('/bdsa/region/template', regionData);
                if (response.status !== 200) {
                    const errorMessage = response.data?.message || 
                                       response.data?.error || 
                                       (typeof response.data === 'string' ? response.data : null) ||
                                       response.data?.statusText ||
                                       `Failed to create region definition (${response.status})`;
                    const error = new Error(errorMessage);
                    error.response = response;
                    throw error;
                }
                return response;
            }
        },
        onSuccess: () => {
            // Save orientation preferences if checkbox is checked (create mode only)
            if (!isEditMode && rememberOrientation) {
                try {
                    const preferences = {
                        hemisphere: formData.schemaFields.hemisphere || '',
                        sliceOrientation: formData.schemaFields.sliceOrientation || '',
                    };
                    localStorage.setItem('bdsa_region_orientation_preferences', JSON.stringify(preferences));
                } catch (e) {
                    console.error('Error saving region orientation preferences:', e);
                }
            }
            
            queryClient.invalidateQueries(['bdsa-region']);
            if (onComplete) {
                onComplete();
            }
        },
        onError: (err) => {
            const errorMessage = err.message || 
                               err.response?.data?.message ||
                               err.response?.data?.error ||
                               (isEditMode ? 'Failed to update region definition' : 'Failed to create region definition');
            setError(errorMessage);
        },
    });

    const deleteRegionMutation = useMutation({
        mutationFn: async () => {
            const response = await api.delete(`/bdsa/region/template/${initialItem._id}`);
            if (response.status !== 200) {
                const errorMessage = response.data?.message || 
                                   response.data?.error || 
                                   (typeof response.data === 'string' ? response.data : null) ||
                                   response.data?.statusText ||
                                   `Failed to delete region definition (${response.status})`;
                const error = new Error(errorMessage);
                error.response = response;
                throw error;
            }
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['bdsa-region']);
            if (onComplete) {
                onComplete();
            }
        },
        onError: (err) => {
            const errorMessage = err.message || 
                               err.response?.data?.message ||
                               err.response?.data?.error ||
                               'Failed to delete region definition';
            setError(errorMessage);
        },
    });

    // Get available regions from latest schema
    const availableRegions = useMemo(() => {
        if (!latestSchema) return [];
        return Object.entries(latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties || {})
            .map(([key, value]) => ({
                key,
                title: value.title || key,
                abbreviation: value.abbreviation || '',
                regionCategory: value.regionCategory || '',
            }));
    }, [latestSchema]);

    // Map schema region keys to existing region definitions
    const existingRegionKeys = useMemo(() => {
        if (regionsResponse?.status !== 200 || !Array.isArray(regionsResponse.data)) {
            return new Set();
        }
        const keys = new Set();
        regionsResponse.data.forEach(region => {
            if (region.schemaRegionKey) {
                keys.add(region.schemaRegionKey);
            } else if (region.details && typeof region.details === 'object') {
                Object.keys(region.details).forEach(key => {
                    keys.add(key);
                });
            }
        });
        return keys;
    }, [regionsResponse]);

    // Get available landmarks for selected region
    const availableLandmarks = useMemo(() => {
        if (!selectedRegionKey || !latestSchema) return [];
        const regionDef = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties[selectedRegionKey];
        return regionDef?.items?.enum || [];
    }, [selectedRegionKey, latestSchema]);

    // Extract additional schema properties (excluding regions)
    const regionSchemaProperties = useMemo(() => {
        if (!latestSchema) return {};
        const regionIdsProps = latestSchema.definition?.properties?.bdsa_region_ids?.properties || {};
        // Exclude 'regions' as it's already handled for landmarks
        const { regions, ...otherProps } = regionIdsProps;
        return otherProps;
    }, [latestSchema]);

    // Extract schema properties for "Other" and "Lesion" categories
    const otherLesionSchemaProperties = useMemo(() => {
        if (!selectedRegionKey || !latestSchema) return {};
        const isOtherOrLesion = selectedRegionKey === 'Other' || selectedRegionKey === 'Lesion';
        if (!isOtherOrLesion) return {};
        const regionDef = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties[selectedRegionKey];
        return regionDef?.properties || {};
    }, [selectedRegionKey, latestSchema]);

    // Initialize form data from initialItem if editing, or from schemaKey if provided
    useEffect(() => {
        if (initialItem && latestSchema) {
            const regionKey = initialItem.schemaRegionKey || '';

            setFormData({
                displayName: initialItem.displayName ?? initialItem.longName ?? '',
                abbreviation: initialItem.abbreviation ?? initialItem.name ?? '',
                description: initialItem.description || '',
                selectedLandmarks: Array.isArray(initialItem.details?.landmarks) ? initialItem.details.landmarks : [],
                schemaFields: {
                    hemisphere: initialItem.details?.hemisphere || '',
                    sliceOrientation: initialItem.details?.sliceOrientation || '',
                    damage: initialItem.details?.damage || [],
                },
                otherLesionFields: {
                    regionCategory: initialItem.details?.regionCategory || '',
                },
            });

            if (regionKey) {
                setSelectedRegionKey(regionKey);
            }

            if (initialItem.details?.damage && Array.isArray(initialItem.details.damage) && initialItem.details.damage.length > 0) {
                setDamageEnabled(true);
            }
        } else if (schemaKeyFromUrl && latestSchema && !isEditMode) {
            setSelectedRegionKey(schemaKeyFromUrl);
        }
    }, [initialItem?._id, latestSchema, schemaKeyFromUrl, isEditMode]);

    // Auto-fill form when region is selected (only in create mode)
    useEffect(() => {
        if (!isEditMode && selectedRegionKey && latestSchema) {
            const region = availableRegions.find(r => r.key === selectedRegionKey);
            if (region) {
                const isOtherOrLesion = selectedRegionKey === 'Other' || selectedRegionKey === 'Lesion';
                setFormData(prev => ({
                    ...prev,
                    displayName: isOtherOrLesion ? '' : region.title,
                    abbreviation: isOtherOrLesion ? '' : (region.abbreviation || region.key),
                    description: '',
                    selectedLandmarks: [],
                    otherLesionFields: {
                        regionCategory: '',
                    },
                }));
            }
        }
    }, [selectedRegionKey, latestSchema, availableRegions, isEditMode]);

    // Stable navigation function - must be before any early returns
    const goToStep = useCallback((step) => {
        setCurrentStep(step); // Update local state immediately
        const params = new URLSearchParams(searchParams);
        params.set('wizardStep', step.toString());
        // Preserve wizard, editId, and schemaKey if they exist
        if (searchParams.get('wizard')) {
            params.set('wizard', searchParams.get('wizard'));
        }
        if (searchParams.get('editId')) {
            params.set('editId', searchParams.get('editId'));
        }
        if (searchParams.get('schemaKey')) {
            params.set('schemaKey', searchParams.get('schemaKey'));
        }
        navigate(`/manage/region?${params.toString()}`);
    }, [searchParams, navigate]);

    // Use ref for goToStep to avoid dependency issues in onClick handlers - must be before any early returns
    const goToStepRef = useRef(goToStep);
    goToStepRef.current = goToStep;

    // Ensure we're on step 2 in edit mode (safety check) - must be after goToStepRef is set up
    useEffect(() => {
        if (isEditMode && currentStep === 1) {
            goToStepRef.current(2);
        }
    }, [isEditMode, currentStep]);

    // Sync with URL on mount/URL change (but only if different) - must be before any early returns
    const urlStepValue = searchParams.get('wizardStep');
    useEffect(() => {
        if (urlStepValue) {
            const stepNum = parseInt(urlStepValue);
            // In edit mode, never allow step 1
            if (isEditMode && stepNum === 1) {
                // Force step 2 in edit mode
                setCurrentStep(2);
                const params = new URLSearchParams(searchParams);
                params.set('wizardStep', '2');
                navigate(`/manage/region?${params.toString()}`, { replace: true });
            } else if (stepNum !== currentStep && stepNum >= 1 && stepNum <= 2) {
                setCurrentStep(stepNum);
            }
        } else if (isEditMode && currentStep === 1) {
            // If no URL step and we're in edit mode at step 1, go to step 2
            setCurrentStep(2);
            const params = new URLSearchParams(searchParams);
            params.set('wizardStep', '2');
            navigate(`/manage/region?${params.toString()}`, { replace: true });
        }
    }, [urlStepValue, currentStep, isEditMode, searchParams, navigate]);

    const toggleLandmark = (landmark) => {
        setFormData(prev => {
            const isSelected = prev.selectedLandmarks.includes(landmark);
            return {
                ...prev,
                selectedLandmarks: isSelected
                    ? prev.selectedLandmarks.filter(l => l !== landmark)
                    : [...prev.selectedLandmarks, landmark],
            };
        });
    };

    const renderSchemaField = (key, prop) => {
        const value = formData.schemaFields[key] || (prop.type === 'array' ? [] : '');
        const mandatorySchemaFields = ['hemisphere', 'sliceOrientation'];
        const isRequired = mandatorySchemaFields.includes(key);
        const fieldError = fieldErrors[`schemaField_${key}`];
        const isValid = !fieldError && (isRequired ? (Array.isArray(value) ? value.length > 0 : value) : true);
        
        if (prop.enum && prop.type !== 'array') {
            // Single enum value (hemisphere, sliceOrientation)
            return (
                <div key={key} className={styles.formField}>
                    <label className={isRequired ? styles.requiredLabel : ''}>
                        {prop.title || key} {isRequired && '*'}
                    </label>
                    <div className={styles.inputWrapper}>
                        <select
                            value={value}
                            onChange={(e) => {
                                setFormData(prev => ({
                                    ...prev,
                                    schemaFields: { ...prev.schemaFields, [key]: e.target.value }
                                }));
                                validateField(`schemaField_${key}`, e.target.value);
                            }}
                            onBlur={() => validateField(`schemaField_${key}`, value)}
                            className={fieldError ? styles.fieldError : (isValid && isRequired ? styles.fieldValid : '')}
                        >
                            <option value="">-- Select --</option>
                            {prop.enum.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        {isValid && isRequired && <span className={styles.fieldValidIcon}>✓</span>}
                    </div>
                    {fieldError && <span className={styles.fieldErrorMessage}>{fieldError}</span>}
                    {prop.description && <small>{prop.description}</small>}
                </div>
            );
        } else if (prop.type === 'array' && prop.items?.enum) {
            // Array of enum values (damage)
            const isDamage = key === 'damage';
            return (
                <div key={key} className={styles.formField}>
                    <label className={isRequired ? styles.requiredLabel : ''}>
                        {prop.title || key} {isRequired && '*'}
                        {!isRequired && isDamage && <span style={{fontWeight: 'normal', color: '#6a737d'}}> (Optional)</span>}
                    </label>
                    <div className={styles.checkboxGroup}>
                        {prop.items.enum.map(opt => {
                            const isChecked = value.includes(opt);
                            return (
                                <label key={opt} className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            const newValue = e.target.checked
                                                ? [...value, opt]
                                                : value.filter(v => v !== opt);
                                            setFormData(prev => ({
                                                ...prev,
                                                schemaFields: { ...prev.schemaFields, [key]: newValue }
                                            }));
                                            validateField(`schemaField_${key}`, newValue);
                                        }}
                                    />
                                    {opt}
                                </label>
                            );
                        })}
                    </div>
                    {fieldError && <span className={styles.fieldErrorMessage}>{fieldError}</span>}
                    {isValid && isRequired && <span className={styles.fieldValidIcon}>✓</span>}
                    {prop.description && <small>{prop.description}</small>}
                </div>
            );
        }
        return null;
    };

    // Show error if no schema
    if (!schemasLoading && !latestSchema) {
        return (
            <SetupPageLayout
                stepNumber={1}
                totalSteps={1}
                title="Create Region Definition"
                description="Pre-define conventional options for a schema region. This definition will be available for mapping to block labels in block definitions, protocols, and case creation."
                onPrevious={onCancel}
                onNext={onCancel}
                nextLabel="Back"
            >
                <div className={styles.error}>No published schema found. Please publish a schema first.</div>
            </SetupPageLayout>
        );
    }

    const handleNext = () => {
        if (currentStep === 1) {
            if (!selectedRegionKey) {
                setError('Please select a region from the schema');
                return;
            }
            goToStep(2);
            setError(null);
        } else if (currentStep === 2) {
            if (validateAllFields()) {
                handleCreate();
            } else {
                setError('Please fix the errors below');
            }
        }
    };

    const handleBack = () => {
        // In edit mode we only show step 2; Back should close the wizard
        if (isEditMode) {
            onCancel();
            return;
        }
        if (currentStep > 1) {
            goToStep(currentStep - 1);
            setError(null);
        } else {
            onCancel();
        }
    };

    const buildDetailsObject = () => {
        const isOtherOrLesion = selectedRegionKey === 'Other' || selectedRegionKey === 'Lesion';
        const details = {
            landmarks: !isOtherOrLesion ? (Array.isArray(formData.selectedLandmarks) ? formData.selectedLandmarks : []) : [],
            hemisphere: formData.schemaFields.hemisphere || null,
            sliceOrientation: formData.schemaFields.sliceOrientation || null,
            damage: formData.schemaFields.damage.length > 0 ? formData.schemaFields.damage : null,
            regionCategory: isOtherOrLesion && formData.otherLesionFields.regionCategory ? formData.otherLesionFields.regionCategory : null,
        };
        return details;
    };

    const handleCreate = () => {
        if (!validateAllFields()) {
            setError('Please fix the errors below');
            return;
        }

        const details = buildDetailsObject();

        const regionData = {
            query: {
                abbreviation: formData.abbreviation,
            },
            body: {
                display_name: formData.displayName,
                schema_region_key: selectedRegionKey,
                schema_id: latestSchema._id,
                description: formData.description || '',
                details: details,
                used_from: null,
                used_to: null,
            },
        };

        createRegionMutation.mutate(regionData);
    };

    const handleDelete = () => {
        if (window.confirm(`Are you sure you want to delete "${formData.displayName || initialItem?.displayName || formData.abbreviation || initialItem?.abbreviation}"? This action cannot be undone.`)) {
            deleteRegionMutation.mutate();
        }
    };

    // Validation functions
    const validateField = (fieldName, value) => {
        const errors = { ...fieldErrors };
        if (fieldName === 'displayName') {
            if (!value || !value.trim()) {
                errors.displayName = 'Display name is required';
            } else {
                delete errors.displayName;
            }
        } else if (fieldName === 'abbreviation') {
            if (!value || !value.trim()) {
                errors.abbreviation = 'Abbreviation is required';
            } else {
                delete errors.abbreviation;
            }
        } else if (fieldName.startsWith('schemaField_')) {
            const schemaKey = fieldName.replace('schemaField_', '');
            const schemaPropertyKeys = Object.keys(regionSchemaProperties);
            if (schemaPropertyKeys.includes(schemaKey)) {
                const required = latestSchema?.definition?.properties?.bdsa_region_ids?.required || [];
                const mandatorySchemaFields = ['hemisphere', 'sliceOrientation'];
                const isRequired = required.includes(schemaKey) && schemaKey !== 'regions' || mandatorySchemaFields.includes(schemaKey);
                if (isRequired && (!value || (Array.isArray(value) && value.length === 0))) {
                    errors[fieldName] = schemaKey === 'hemisphere' ? 'Hemisphere is required' : schemaKey === 'sliceOrientation' ? 'Slice orientation is required' : 'This field is required';
                } else {
                    delete errors[fieldName];
                }
            } else {
                delete errors[fieldName];
            }
        }
        setFieldErrors(errors);
        return !errors[fieldName];
    };

    const validateAllFields = () => {
        const errors = {};
        if (!formData.displayName || !formData.displayName.trim()) {
            errors.displayName = 'Display name is required';
        }
        if (!formData.abbreviation || !formData.abbreviation.trim()) {
            errors.abbreviation = 'Abbreviation is required';
        }
        if (latestSchema) {
            const required = latestSchema.definition?.properties?.bdsa_region_ids?.required || [];
            const schemaPropertyKeys = Object.keys(regionSchemaProperties);
            const mandatorySchemaFields = ['hemisphere', 'sliceOrientation'];
            [...required.filter(key => key !== 'regions' && schemaPropertyKeys.includes(key)), ...mandatorySchemaFields]
                .filter((key, i, arr) => arr.indexOf(key) === i)
                .forEach(key => {
                    if (!schemaPropertyKeys.includes(key)) return;
                    const value = formData.schemaFields[key];
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        errors[`schemaField_${key}`] = key === 'hemisphere' ? 'Hemisphere is required' : key === 'sliceOrientation' ? 'Slice orientation is required' : 'This field is required';
                    }
                });
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const canProceed = () => {
        if (isEditMode) {
            if (currentStep === 2) {
                const base = formData.displayName?.trim() && formData.abbreviation?.trim();
                const hemisphereOk = !regionSchemaProperties.hemisphere || (formData.schemaFields?.hemisphere || '').trim();
                const orientationOk = !regionSchemaProperties.sliceOrientation || (formData.schemaFields?.sliceOrientation || '').trim();
                return base && hemisphereOk && orientationOk;
            }
            return true;
        }
        if (currentStep === 1) return selectedRegionKey !== '';
        if (currentStep === 2) {
            const base = formData.displayName?.trim() && formData.abbreviation?.trim();
            const hemisphereOk = !regionSchemaProperties.hemisphere || (formData.schemaFields?.hemisphere || '').trim();
            const orientationOk = !regionSchemaProperties.sliceOrientation || (formData.schemaFields?.sliceOrientation || '').trim();
            return base && hemisphereOk && orientationOk;
        }
        return true;
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                if (isEditMode) {
                    // Skip step 1 in edit mode, go directly to step 2
                    return null;
                }
                return (
                    <div className={styles.stepContent}>
                        <h3>Select Region</h3>
                        <p>Choose a region from the schema. The name will be auto-filled.</p>
                        <div style={{fontSize: '13px', color: '#6a737d', marginTop: '0.5em', padding: '0.75em', background: '#f6f8fa', borderRadius: '6px', border: '1px solid #e1e4e8'}}>
                            <strong>About region definitions:</strong> Region definitions are based on entries in the region schema. 
                            The short name (abbreviation) comes from the schema and cannot be changed. 
                            Definitions add local details like landmarks, hemisphere, and slice orientation. 
                            To add new regions to the schema, use the schema editor.
                        </div>
                        {availableRegions.length === 0 ? (
                            <div className={styles.error}>No regions found in the selected schema.</div>
                        ) : (
                            <div className={styles.regionList}>
                                {availableRegions.map(region => (
                                    <div
                                        key={region.key}
                                        className={`${styles.regionCard} ${selectedRegionKey === region.key ? styles.selected : ''} ${existingRegionKeys.has(region.key) ? styles.hasDefinition : ''}`}
                                        onClick={() => {
                                            setSelectedRegionKey(region.key);
                                            setError(null);
                                            goToStep(2);
                                        }}
                                    >
                                        <div className={styles.regionCardHeader}>
                                            <strong>{region.title}</strong>
                                            {existingRegionKeys.has(region.key) && (
                                                <span className={styles.definedBadge} title="Region definition already exists">
                                                    ✓ Defined
                                                </span>
                                            )}
                                        </div>
                                        <span className={styles.abbreviation}>({region.abbreviation})</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 2:
                const selectedRegion = availableRegions.find(r => r.key === selectedRegionKey);
                const schemaRegionName = selectedRegion?.title || '';
                const schemaRegionAbbr = selectedRegion?.abbreviation || '';
                const schemaRegionCategory = selectedRegion?.regionCategory || '';
                const isOtherOrLesion = selectedRegionKey === 'Other' || selectedRegionKey === 'Lesion';
                return (
                    <div className={styles.stepContent}>
                        <h3>Configure Details</h3>
                        <p>Review and edit the region definition details.</p>
                        <div style={{fontSize: '13px', color: '#6a737d', marginBottom: '1em', padding: '0.75em', background: '#f6f8fa', borderRadius: '6px', border: '1px solid #e1e4e8'}}>
                            <strong>How region definitions are used:</strong> These definitions pre-configure conventional options (sectioning orientation, landmarks) for schema regions. They will be used in block definitions, protocols, and during case creation to automatically add accurate metadata to slides.
                        </div>
                        <div className={styles.form}>
                            {/* Schema region (read-only) */}
                            <div className={styles.schemaInfo}>
                                <span className={styles.schemaInfoLabel}>Schema region:</span>
                                <span className={styles.schemaInfoName}>{schemaRegionName}</span>
                                {schemaRegionAbbr && (
                                    <>
                                        <span className={styles.schemaInfoSeparator}>|</span>
                                        <span className={styles.schemaInfoAbbr}>{schemaRegionAbbr}</span>
                                    </>
                                )}
                                {schemaRegionCategory && (
                                    <>
                                        <span className={styles.schemaInfoSeparator}>|</span>
                                        <span className={styles.schemaInfoType}>{schemaRegionCategory}</span>
                                    </>
                                )}
                            </div>

                            {/* Hemisphere and Slice Orientation - mandatory, at top */}
                            {(regionSchemaProperties.hemisphere || regionSchemaProperties.sliceOrientation) && (
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <h4>Hemisphere &amp; Orientation *</h4>
                                            <small style={{display: 'block', marginTop: '0.25em'}}>Required sectioning options for this region</small>
                                        </div>
                                        {!isEditMode && (
                                            <label className={styles.rememberCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    checked={rememberOrientation}
                                                    onChange={(e) => setRememberOrientation(e.target.checked)}
                                                />
                                                <span>Remember selection</span>
                                            </label>
                                        )}
                                    </div>
                                    <div className={styles.sectionContent}>
                                        {regionSchemaProperties.hemisphere && renderSchemaField('hemisphere', regionSchemaProperties.hemisphere)}
                                        {regionSchemaProperties.sliceOrientation && renderSchemaField('sliceOrientation', regionSchemaProperties.sliceOrientation)}
                                    </div>
                                </div>
                            )}

                            <div className={styles.formField}>
                                <label className={styles.requiredLabel}>Abbreviation *</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        value={formData.abbreviation}
                                        onChange={(e) => {
                                            setFormData({ ...formData, abbreviation: e.target.value });
                                            if (fieldErrors.abbreviation) validateField('abbreviation', e.target.value);
                                        }}
                                        onBlur={() => validateField('abbreviation', formData.abbreviation)}
                                        placeholder="e.g., MB"
                                        disabled={!isOtherOrLesion}
                                        className={fieldErrors.abbreviation ? styles.fieldError : (formData.abbreviation ? styles.fieldValid : '')}
                                    />
                                    {!fieldErrors.abbreviation && formData.abbreviation && <span className={styles.fieldValidIcon}>✓</span>}
                                </div>
                                {fieldErrors.abbreviation && <span className={styles.fieldErrorMessage}>{fieldErrors.abbreviation}</span>}
                                <small>
                                    {isOtherOrLesion
                                        ? 'Enter the abbreviation for this custom region'
                                        : 'Abbreviation from schema (used in filenames and in parentheses on cards)'}
                                </small>
                            </div>

                            <div className={styles.formField}>
                                <label className={styles.requiredLabel}>
                                    {isOtherOrLesion ? 'Region name *' : 'Display name *'}
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        value={formData.displayName}
                                        onChange={(e) => {
                                            setFormData({ ...formData, displayName: e.target.value });
                                            if (fieldErrors.displayName) validateField('displayName', e.target.value);
                                        }}
                                        onBlur={() => validateField('displayName', formData.displayName)}
                                        placeholder={isOtherOrLesion ? "Enter custom region name" : "e.g., Midbrain"}
                                        className={fieldErrors.displayName ? styles.fieldError : (formData.displayName ? styles.fieldValid : '')}
                                    />
                                    {!fieldErrors.displayName && formData.displayName && <span className={styles.fieldValidIcon}>✓</span>}
                                </div>
                                {fieldErrors.displayName && <span className={styles.fieldErrorMessage}>{fieldErrors.displayName}</span>}
                                <small>
                                    {isOtherOrLesion
                                        ? 'Enter the name for this custom region definition'
                                        : 'Display name shown as the main label on cards'}
                                </small>
                            </div>

                            {/* Landmarks Section - Only show for non-Other/Lesion regions */}
                            {!isOtherOrLesion && availableLandmarks.length > 0 && (
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <h4>Landmarks (Optional)</h4>
                                            <small style={{display: 'block', marginTop: '0.25em'}}>Anatomical landmarks typically present in this region</small>
                                        </div>
                                        <small>{formData.selectedLandmarks.length} of {availableLandmarks.length} selected</small>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        <div className={styles.landmarksList}>
                                            {availableLandmarks.map(landmark => (
                                                <label key={landmark} className={styles.landmarkCheckbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.selectedLandmarks.includes(landmark)}
                                                        onChange={() => toggleLandmark(landmark)}
                                                    />
                                                    <span>{landmark}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <small>Select which anatomical landmarks are present in this region</small>
                                    </div>
                                </div>
                            )}

                            <div className={styles.formField}>
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => {
                                        setFormData({ ...formData, description: e.target.value });
                                    }}
                                    placeholder="Optional description displayed to users when choosing regions"
                                    rows={3}
                                    className={styles.textarea}
                                />
                                <small>Optional description for this region definition</small>
                            </div>

                            {/* Damage Field with Toggle */}
                            {regionSchemaProperties.damage && (
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <h4>Damage (Optional)</h4>
                                            <small style={{display: 'block', marginTop: '0.25em'}}>Specifies tissue damage in this region and the cause of it</small>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.toggleButton}
                                            onClick={() => setDamageEnabled(!damageEnabled)}
                                        >
                                            {damageEnabled ? 'Disable' : 'Enable'} Damage
                                        </button>
                                    </div>
                                    {damageEnabled && (
                                        <div className={styles.sectionContent}>
                                            {renderSchemaField('damage', regionSchemaProperties.damage)}
                                            <small style={{marginTop: '0.5em', display: 'block', color: '#6a737d'}}>
                                                <strong>Note:</strong> Damage is typically not consistent across region definitions and should only be specified when damage is a defining characteristic of the region template. For most region definitions, leave this disabled.
                                            </small>
                                        </div>
                                    )}
                                    {!damageEnabled && (
                                        <div className={styles.sectionContent}>
                                            <p style={{color: '#6a737d', fontStyle: 'italic', margin: 0}}>
                                                Damage fields are disabled by default. Enable to specify tissue damage types that are consistently present in this region definition.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Other/Lesion Schema Properties (regionCategory) */}
                            {isOtherOrLesion && (
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <h4>{selectedRegionKey === 'Other' ? 'Other Region' : 'Lesion'} Category</h4>
                                            <small style={{display: 'block', marginTop: '0.25em'}}>
                                                {selectedRegionKey === 'Other' 
                                                    ? 'Use "Other" for regions not covered by standard anatomical regions'
                                                    : 'Use "Lesion" for pathological lesions or abnormal tissue'}
                                            </small>
                                        </div>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        {Object.entries(otherLesionSchemaProperties).map(([key, prop]) => {
                                            if (key === 'name' || key === 'description') return null; // Already handled above
                                            const value = formData.otherLesionFields[key] || (prop.type === 'array' ? [] : '');
                                            const fieldError = fieldErrors[`otherLesionField_${key}`];
                                            
                                            if (prop.enum && prop.type !== 'array') {
                                                return (
                                                    <div key={key} className={styles.formField}>
                                                        <label>
                                                            {prop.title || key}
                                                        </label>
                                                        <div className={styles.inputWrapper}>
                                                            <select
                                                                value={value}
                                                                onChange={(e) => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        otherLesionFields: { ...prev.otherLesionFields, [key]: e.target.value }
                                                                    }));
                                                                    validateField(`otherLesionField_${key}`, e.target.value);
                                                                }}
                                                                onBlur={() => validateField(`otherLesionField_${key}`, value)}
                                                                className={fieldError ? styles.fieldError : ''}
                                                            >
                                                                <option value="">-- Select --</option>
                                                                {prop.enum.map(opt => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        {fieldError && <span className={styles.fieldErrorMessage}>{fieldError}</span>}
                                                        {prop.description && <small>{prop.description}</small>}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const selectedRegionName = availableRegions.find(r => r.key === selectedRegionKey)?.title || '';

    const nextLabelValue = currentStep === 2 
        ? (createRegionMutation.isPending 
            ? 'Saving...' 
            : 'Save and Return')
        : (selectedRegionName ? `Continue with ${selectedRegionName}` : 'Continue');

    return (
        <SetupPageLayout
            stepNumber={currentStep}
            totalSteps={2}
            title={isEditMode ? "Edit Region Definition" : "Create Region Definition"}
            description={isEditMode ? "Edit the region definition" : "Create a new region definition based on the latest published schema"}
            showHeaderNavigation={true}
            backLink="/manage/region"
            backLinkText="Region Setup"
            onPrevious={currentStep > 1 ? handleBack : onCancel}
            onNext={handleNext}
            nextDisabled={!canProceed() || createRegionMutation.isPending}
            nextLabel={nextLabelValue}
            previousLabel={currentStep > 1 ? 'Back' : 'Cancel'}
        >
            {error && <div className={styles.error}>{error}</div>}
            {renderStep()}
            {currentStep === 2 && (
                <WizardFooter
                    onPrevious={currentStep > 1 ? handleBack : null}
                    onNext={handleNext}
                    nextDisabled={!canProceed() || createRegionMutation.isPending}
                    nextLabel={createRegionMutation.isPending 
                        ? 'Saving...' 
                        : 'Save and Return'}
                    previousLabel={currentStep > 1 ? 'Back' : 'Cancel'}
                    showPrevious={currentStep > 1}
                    isPrimaryAction={true}
                    onDelete={isEditMode ? handleDelete : null}
                    deleteDisabled={isEditMode && deleteRegionMutation.isPending}
                    deleteLabel={isEditMode && deleteRegionMutation.isPending ? 'Deleting...' : 'Delete'}
                />
            )}
        </SetupPageLayout>
    );
}

export default RegionDefinitionWizard;
